/**
 * Materials List Component
 *
 * Displays the Bill of Materials in a formatted table.
 * Shows liner feet and total sqft for panels.
 * Can optionally hide prices (unit price and total columns).
 */

import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BillOfMaterials, Currency } from '../../types';
import { getCustomFieldConfig, getCustomFieldsForProduct, getOutputLayout } from '../../data/productTable';
import { buildBomTable, pickLayout, RenderCtx } from '../../utils/renderModel';
import { bomSections, BomSection, BomSectionIcon } from '../../data/bomSections';
import { RenderTable } from './RenderTable';
import {
  SquareStack,
  Grid3X3,
  Minus,
  CircleX,
} from 'lucide-react';
import { CheckboxChecked, CheckboxUnchecked } from '../common/CheckboxIcons';
import './MaterialsList.css';

interface MaterialsListProps {
  billOfMaterials: BillOfMaterials;
  currency: Currency;
  showPrices?: boolean;
  /** BOM-scoped controls rendered right-aligned in the title bar (e.g. the
   *  Export Data icon; room for print/copy later). */
  headerActions?: React.ReactNode;
}

/** Section heading icon, keyed off the section's stable identity. */
const SECTION_ICONS: Record<BomSectionIcon, React.ReactElement> = {
  panel: <SquareStack size={18} />,
  slatwall: <Grid3X3 size={18} />,
  trim: <Minus size={18} />,
};

export const MaterialsList: React.FC<MaterialsListProps> = ({
  billOfMaterials,
  currency,
  showPrices = true,
  headerActions,
}) => {
  const { t } = useTranslation('results');
  // Shared context for the render model (currency, price gating, live fields).
  const ctx: RenderCtx = {
    currency,
    showPrices,
    getFields: getCustomFieldsForProduct,
  };
  const fieldConfig = getCustomFieldConfig();
  const bomLayout = pickLayout(getOutputLayout(), 'bom');

  // Sections, their headings and the continuous line numbering all come from
  // data/bomSections.ts, shared with the PDF and CSV exporters.
  const renderMaterialTable = (section: BomSection) => {
    if (section.items.length === 0) return null;

    const table = buildBomTable(
      section.items, section.isPanel, ctx, fieldConfig, bomLayout, section.startLineNo,
    );

    return (
      <div className="materials-section" key={section.key}>
        <h3 className="materials-section-title">
          {SECTION_ICONS[section.icon]}
          {t(section.titleKey)}
        </h3>
        <RenderTable table={table} ctx={ctx} />
      </div>
    );
  };

  return (
    <div className="materials-list">
      <div className="materials-header">
        <h2 className="materials-title">{t('bom.title')}</h2>
        {headerActions && <div className="bom-header-actions">{headerActions}</div>}
      </div>

      {bomSections(billOfMaterials).map(renderMaterialTable)}

      {/* Screws Section - Combined total only */}
      <div className="materials-section">
        <h3 className="materials-section-title">
          <CircleX size={18} />
          {t('bom.hardware.title')}
        </h3>
        <div className="screws-summary">
          <div className="screws-row total">
            <span className="screws-label">{t('bom.hardware.totalScrews')}</span>
            <span className="screws-value">
              {billOfMaterials.screws.total.toLocaleString()}
            </span>
          </div>
          <p className="screws-note">{t('bom.hardware.screwsNote')}</p>
        </div>

        {/* Static reference: screw recommendations by receiving wall type */}
        <div className="screws-reference">
          <p className="screws-reference-intro">
            {/* Named tags, not <Trans>'s default numeric ones: a translator
                can see that <a> is the link. The empty expression child keeps
                jsx-a11y/anchor-has-content satisfied — Trans supplies the real
                text — and the parity check verifies both tags survive
                translation. */}
            <Trans
              i18nKey="bom.hardware.reference.intro"
              ns="results"
              components={{
                b: <strong />,
                a: <a href="https://trusscore.com/screws" target="_blank" rel="noopener noreferrer">{}</a>,
              }}
            />
          </p>
          <div className="table-responsive">
            <table className="table table-bordered table-striped screws-reference-table">
              <thead className="table-tc-slate">
                <tr>
                  <th className="text-center col-check"><CheckboxChecked /></th>
                  <th>{t('bom.hardware.reference.receivingWall')}</th>
                  <th>{t('bom.hardware.reference.recommendation')}</th>
                  <th className="text-center">{t('bom.hardware.reference.screwLength')}</th>
                </tr>
              </thead>
              <tbody className="table-group-divider">
                {/* Screw lengths stay as written: they are imperial hardware
                    sizes, not prose. fr-CA construction runs in inches too. */}
                <tr>
                  <td className="text-center col-check"><CheckboxUnchecked /></td>
                  <td>{t('bom.hardware.reference.walls.woodStud')}</td>
                  <td rowSpan={4} className="screw-rec-cell">
                    {t('bom.hardware.reference.lowProfileScrews')}
                  </td>
                  <td className="text-center">1 &frac14;&quot;</td>
                </tr>
                <tr>
                  <td className="text-center col-check"><CheckboxUnchecked /></td>
                  <td>{t('bom.hardware.reference.walls.drywall')}</td>
                  <td className="text-center">1-3/4&quot; to 2&quot;</td>
                </tr>
                <tr>
                  <td className="text-center col-check"><CheckboxUnchecked /></td>
                  <td>{t('bom.hardware.reference.walls.furringStrips')}</td>
                  <td className="text-center">2 &frac12;&quot;</td>
                </tr>
                <tr>
                  <td className="text-center col-check"><CheckboxUnchecked /></td>
                  <td>{t('bom.hardware.reference.walls.plaster')}</td>
                  <td className="text-center">2&quot; to 2 &frac12;&quot;</td>
                </tr>
                <tr>
                  <td className="text-center col-check"><CheckboxUnchecked /></td>
                  <td>{t('bom.hardware.reference.walls.concrete')}</td>
                  <td>{t('bom.hardware.reference.tapcon')}</td>
                  <td className="text-center">1 &frac14;&quot;</td>
                </tr>
                <tr>
                  <td className="text-center col-check"><CheckboxUnchecked /></td>
                  <td>{t('bom.hardware.reference.walls.metalStud')}</td>
                  <td>{t('bom.hardware.reference.selfTapping')}</td>
                  <td className="text-center">1 &frac14;&quot;</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialsList;
